import {
  Flex,
  Input,
  Button,
  Divider,
  AbsoluteCenter,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { ShowSkipStop } from '../../components/ShowSkipStop';
import { useRef } from 'react';
import { useRestartOutreachCandidatesMutation } from '@/store/query/mco-candidates';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { KText } from 'ui-library/components';
import { useShowToast } from '@/hooks/useShowToast';

export const contactSchema = yup.object({
  email: yup.string().email('Email is not valid').required('Email is required'),
});

export const EmailNotFound = ({ restartDetails = {}, onStopOpen }) => {
  const formRef = useRef();
  const toast = useShowToast();
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();

  const onSubmit = async (values) => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          email: values?.email,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {}
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-email-not-found"
    >
      <KText size="md-medium" color="secondary-900">
        Email Not Found
      </KText>
      <KText>
        {`We couldn't find the email address for this Candidate. You can restart
        the "email step" by entering the candidate's email id below or ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'you can choose to skip email steps and perform LinkedIn steps'
            : 'you can choose to stop the flow'
        }`}
      </KText>
      <Formik
        validateOnBlur={false}
        innerRef={formRef}
        initialValues={{
          email: '',
        }}
        validationSchema={contactSchema}
        onSubmit={onSubmit}
      >
        <Form noValidate>
          <Field name="email">
            {({ field, form }) => (
              <FormControl
                mb="16px"
                isRequired
                isInvalid={form.errors.email && form.touched.email}
              >
                <Input
                  {...field}
                  id="email"
                  placeholder="Candidate's Email"
                  data-test-id="flow-restart-action-email-not-found-input"
                />
                <FormErrorMessage>
                  <ErrorMessage name="email" />
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          <Button
            width="100%"
            type="submit"
            isLoading={isLoading}
            data-test-id="flow-restart-action-email-not-found-cta"
          >
            Restart Flow
          </Button>
        </Form>
      </Formik>
      <Flex pos="relative" p="12px">
        <Divider borderColor="border-color" />
        <AbsoluteCenter bg="white" px="36px" fontSize="16px" lineHeight="20px">
          OR
        </AbsoluteCenter>
      </Flex>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const EmailBounced = ({ restartDetails = {}, onStopOpen, contact }) => {
  const formRef = useRef();
  const toast = useShowToast();
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();

  const onSubmit = async (values) => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          email: values?.email,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {}
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-email-bounced"
    >
      <KText size="md-medium" color="secondary-900">
        Email bounced
      </KText>
      <KText>
        {`Email id associated with the candidate is not valid. You can enter an
        alternate email id below to restart the email step or ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'you can choose to skip email steps and perform LinkedIn steps'
            : 'you can choose to stop the flow'
        }`}
      </KText>
      <Formik
        validateOnBlur={false}
        innerRef={formRef}
        initialValues={{
          email: contact?.email,
        }}
        validationSchema={contactSchema}
        onSubmit={onSubmit}
      >
        {({ values = {} }) => (
          <Form noValidate>
            <Field name="email">
              {({ field, form }) => (
                <FormControl
                  mb="16px"
                  isRequired
                  isInvalid={form.errors.email && form.touched.email}
                >
                  <Input
                    {...field}
                    id="email"
                    placeholder="Candidate's Email"
                    data-test-id="flow-restart-action-email-bounced-input"
                  />
                  <FormErrorMessage>
                    <ErrorMessage name="email" />
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Button
              width="100%"
              type="submit"
              isLoading={isLoading}
              disabled={values?.email === contact?.email}
              data-test-id="flow-restart-action-email-bounced-cta"
            >
              Restart Flow
            </Button>
          </Form>
        )}
      </Formik>
      <Flex pos="relative" p="12px">
        <Divider borderColor="#C4C4C466" />
        <AbsoluteCenter bg="white" px="36px" fontSize="16px" lineHeight="20px">
          OR
        </AbsoluteCenter>
      </Flex>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};
